// Main-thread RPC client for the compute Web Worker.

import type { Pipeline, ProjectSettings } from '$lib/model/types';
import type {
  ComputeError,
  ComputeRequest,
  ComputeResponse,
} from './worker';

let workerInstance: Worker | null = null;
let nextId = 1;
const pending = new Map<
  number,
  {
    resolve: (r: ComputeResponse) => void;
    reject: (e: Error) => void;
  }
>();

function getWorker(): Worker {
  if (workerInstance) return workerInstance;
  workerInstance = new Worker(new URL('./worker.ts', import.meta.url), {
    type: 'module',
  });
  workerInstance.addEventListener(
    'message',
    (e: MessageEvent<ComputeResponse | ComputeError>) => {
      const msg = e.data;
      const handler = pending.get(msg.id);
      if (!handler) return;
      pending.delete(msg.id);
      if (msg.type === 'result') handler.resolve(msg);
      else handler.reject(new Error(msg.message));
    },
  );
  workerInstance.addEventListener('error', (e) => {
    console.error('[compute worker]', e);
  });
  return workerInstance;
}

export interface DispatchHandle {
  id: number;
  promise: Promise<ComputeResponse>;
  cancel(reason?: string): void;
}

export function dispatchCompute(
  pipeline: Pipeline,
  settings: ProjectSettings,
  sourceBlob: Blob,
  sourceHash: string,
): DispatchHandle {
  const id = nextId++;
  const worker = getWorker();
  const promise = new Promise<ComputeResponse>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    const req: ComputeRequest = {
      type: 'compute',
      id,
      pipeline,
      settings,
      sourceBlob,
      sourceHash,
    };
    worker.postMessage(req);
  });
  return {
    id,
    promise,
    cancel(reason = 'superseded') {
      const handler = pending.get(id);
      if (handler) {
        pending.delete(id);
        handler.reject(new DOMException(reason, 'AbortError'));
      }
    },
  };
}
